import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import Container from '@/components/Container';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDashboardStore } from '../store/useDashboardStore';
import dayjs from 'dayjs';
import DateRangePicker from '@/components/DateRangePicker';

const schema = z.object({
  frequency: z.enum(['daily', 'weekly', 'monthly']),
  timePeriod: z.enum(['7days', '28days', '90days', 'custom']),
});

type FormData = z.infer<typeof schema>;
type TimePeriod = 'custom' | '7days' | '28days' | '90days';
type Frequency = 'daily' | 'weekly' | 'monthly';

export default function Filters() {
  const { setValues, type } = useDashboardStore();
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
  const yesterday = dayjs().subtract(1, 'day');

  const {
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      timePeriod: '28days',
      frequency: type,
    },
  });

  const calculateDateRange = (period: string): { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs } => {
    // Default values
    let startDate = yesterday.subtract(27, 'day').startOf('day');
    let endDate = yesterday;

    switch (period) {
      case '7days':
        startDate = yesterday.subtract(6, 'day').startOf('day');
        endDate = yesterday;
        break;
      case '28days':
        startDate = yesterday.subtract(27, 'day').startOf('day');
        endDate = yesterday;
        break;
      case '90days':
        startDate = yesterday.subtract(89, 'day').startOf('day');
        endDate = yesterday;
        break;
      case 'custom':
        // Use existing dates or default to current range
        if (startDate && endDate) {
          const customStart = startDate;
          const customEnd = endDate;

          if (customStart && customEnd) {
            // Ensure dates are not in the future
            const safeEndDate = customEnd.isAfter(yesterday) ? yesterday : customEnd;
            return { startDate: customStart, endDate: safeEndDate };
          }
        }
        // Use default
        break;
      default:
        // Default values are already set
        break;
    }

    // Ensure end date is not in the future
    endDate = endDate.isAfter(yesterday) ? yesterday : endDate;

    return { startDate, endDate };
  };

  useEffect(() => {
    const range = calculateDateRange('28days');
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setValue('frequency', type);
    setValue('timePeriod', '28days');

    setValues({
      startDate: range.startDate.toDate(),
      endDate: range.endDate.toDate(),
      type,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDates = (start: dayjs.Dayjs | null, end: dayjs.Dayjs | null) => {
    if (start && end) {
      // Ensure end date is not after yesterday
      const validEnd = end.isAfter(yesterday) ? yesterday : end;

      setStartDate(start);
      setEndDate(validEnd);

      setValues({
        startDate: start.toDate(),
        endDate: validEnd.toDate(),
        type: watch('frequency') as Frequency,
      });

      // If dates changed manually, update to custom period
      setValue('timePeriod', 'custom');
    }
  };

  // Handle time period selection
  const handleTimePeriodChange = (value: string) => {
    setValue('timePeriod', value as TimePeriod);

    if (value !== 'custom') {
      const range = calculateDateRange(value);
      setStartDate(range.startDate);
      setEndDate(range.endDate);

      setValues({
        startDate: range.startDate.toDate(),
        endDate: range.endDate.toDate(),
        type: watch('frequency') as Frequency,
      });
    }

    setShowCustomDates(value === 'custom');
  };

  // Watch for frequency changes
  React.useEffect(() => {
    const subscription = watch((data) => {
      if (data.frequency && data.frequency !== type) {
        setValues({
          type: data.frequency as Frequency,
          startDate: startDate?.toDate() || new Date(),
          endDate: endDate?.toDate() || new Date(),
        });
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, startDate, endDate]);

  return (
    <Container className="flex flex-col items-center justify-center gap-4">
      <form className="col-span-4 flex w-full flex-col gap-4 md:col-span-12 md:col-end-11 md:flex-row md:items-end lg:col-start-3">
        <div className="w-full">
          <Select value={watch('timePeriod')} onValueChange={handleTimePeriodChange}>
            <SelectTrigger>
              <SelectValue placeholder="Time Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Last 7 days</SelectItem>
              <SelectItem value="28days">Last 28 days</SelectItem>
              <SelectItem value="90days">Last 90 days</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-full">
          <Select
            value={watch('frequency')}
            onValueChange={(value: Frequency) => {
              setValue('frequency', value);
              setValues({
                startDate: startDate?.toDate() || new Date(),
                endDate: endDate?.toDate() || new Date(),
                type: value,
              });
            }}>
            <SelectTrigger>
              <SelectValue placeholder="Frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
          {errors.frequency && <p>{errors.frequency.message}</p>}
        </div>
      </form>
      <div className="col-span-4 flex w-full flex-col gap-4 md:col-span-12 md:col-end-11 md:flex-row md:items-end lg:col-start-3">
        {showCustomDates && (
          <div className="flex-grow">
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={(date) => updateDates(date, endDate)}
              onEndDateChange={(date) => updateDates(startDate, date)}
              maxDate={yesterday}
            />
          </div>
        )}
      </div>
    </Container>
  );
}
